/**
 * DEFCAT-R2-12 — inheritance-aware allocation sets for the Dependencies tab.
 *
 * Implements the UI half of
 * `_BACKEND/contracts/taxonomy/v1/design-notes/allocation-inheritance.md`. For
 * the selected location node `L`, given the overlay pairs and both trees, it
 * splits the TYPE nodes into three display sets:
 *
 *  - OWN        — include pairs whose locationEntryId === L (solid ticks). Plus
 *                 type-group → leaf expansion (ticking a group lights its leaves
 *                 as OWN-inherited on the TYPE axis, preserved from P6).
 *  - INHERITED  — include pairs on an ANCESTOR of L (location walk-up, incl. the
 *                 L0 link once L0 pairs are legal): faded, un-tickable; unticking
 *                 one writes an EXCLUSION on L.
 *  - EXCLUDED   — exclusion pairs whose locationEntryId === L (node-local): the
 *                 struck/badge state. Re-ticking removes the exclusion.
 *
 * Location walk-up = parentEntryId chain ∪ the synthetic L0 node id the entry
 * hangs under (so an include allocated on the L0 component group is INHERITED by
 * every descendant location — R2-13 read-side, even though L0 *writes* are not
 * yet accepted by the BE).
 *
 * Pure (no React) so the inheritance + exclusion logic is unit-tested against
 * the design note's worked example in isolation.
 */
import type {
  CatalogueValidityPair,
  DefectCatalogueEntryV2,
} from "@aeriosiq/types";
import { l0GroupOfLocationEntry, l0NodeId, isL0NodeId } from "./treeBuild.js";

export interface AllocationSets {
  /** Type entry ids ticked directly on L (or implied by a type-group tick). */
  own: Set<string>;
  /** Type entry ids inherited from a location ancestor of L (faded). */
  inherited: Set<string>;
  /** Type entry ids excluded node-locally on L (struck). */
  excluded: Set<string>;
  /** entryId → nearest ancestor LOCATION node supplying the inherited tick. */
  inheritedSource: Map<string, string>;
}

/** entryId → entry, for one axis. */
function indexAxis(
  entries: DefectCatalogueEntryV2[],
  axis: "type" | "location",
): Map<string, DefectCatalogueEntryV2> {
  const m = new Map<string, DefectCatalogueEntryV2>();
  for (const e of entries) if (e.axis === axis) m.set(e.entryId, e);
  return m;
}

/**
 * Ordered location walk-up chain for `L`: `L`, its parentEntryId ancestors, and
 * the synthetic L0 node id it hangs under (always the chain tail). When `L` is
 * itself a synthetic L0 node, the chain is just `[L]`.
 */
export function locationChain(
  selectedId: string,
  locById: ReadonlyMap<string, DefectCatalogueEntryV2>,
): string[] {
  if (isL0NodeId(selectedId)) return [selectedId];
  const chain: string[] = [];
  const seen = new Set<string>();
  let cur: DefectCatalogueEntryV2 | undefined = locById.get(selectedId);
  while (cur && !seen.has(cur.entryId)) {
    seen.add(cur.entryId);
    chain.push(cur.entryId);
    const pid: string | null | undefined = cur.parentEntryId;
    cur = pid != null && pid.length > 0 ? locById.get(pid) : undefined;
  }
  // Append the L0 the (original) entry belongs to.
  const origin = locById.get(selectedId);
  if (origin) chain.push(l0NodeId(l0GroupOfLocationEntry(origin, locById)));
  return chain;
}

/** Type-axis descendants (self + children) of `typeId`. Cycle-safe. */
function typeDescendants(
  typeId: string,
  childrenOf: ReadonlyMap<string, string[]>,
): string[] {
  const out: string[] = [];
  const stack = [typeId];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const id = stack.pop();
    if (id === undefined || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    for (const c of childrenOf.get(id) ?? []) stack.push(c);
  }
  return out;
}

/**
 * Compute the OWN / INHERITED / EXCLUDED type-node sets for the selected
 * location. `selectedId` may be a real location entryId OR a synthetic L0 node
 * id. Type-group ticks expand to their leaf/descendant type nodes on both the
 * own and inherited sides (design-note rule 4).
 */
export function allocationSetsForLocation(
  pairs: CatalogueValidityPair[] | null | undefined,
  entries: DefectCatalogueEntryV2[],
  selectedId: string | null,
): AllocationSets {
  const empty: AllocationSets = {
    own: new Set(),
    inherited: new Set(),
    excluded: new Set(),
    inheritedSource: new Map(),
  };
  if (selectedId === null) return empty;

  const locById = indexAxis(entries, "location");
  const typeChildren = new Map<string, string[]>();
  for (const e of entries) {
    if (e.axis !== "type") continue;
    const pid = e.parentEntryId;
    if (pid != null && pid.length > 0) {
      const arr = typeChildren.get(pid) ?? [];
      arr.push(e.entryId);
      typeChildren.set(pid, arr);
    }
  }

  const chain = locationChain(selectedId, locById);
  const chainIndex = new Map<string, number>();
  chain.forEach((id, i) => chainIndex.set(id, i));

  const own = new Set<string>();
  const inherited = new Set<string>();
  const excluded = new Set<string>();
  const inheritedSource = new Map<string, string>();

  for (const p of pairs ?? []) {
    const idx = chainIndex.get(p.locationEntryId);
    if (idx === undefined) continue; // not on L's walk-up chain
    const leaves = typeDescendants(p.typeEntryId, typeChildren);
    if (p.exclusion === true) {
      // Exclusions are NODE-LOCAL to L (chain index 0). Ignore ancestor
      // exclusions (design-note rule 3).
      if (idx === 0) for (const t of leaves) excluded.add(t);
    } else if (idx === 0) {
      for (const t of leaves) own.add(t);
    } else {
      for (const t of leaves) {
        inherited.add(t);
        // Keep the NEAREST ancestor source (smallest chain index wins).
        const prev = inheritedSource.get(t);
        if (prev === undefined || (chainIndex.get(prev) ?? Infinity) > idx) {
          inheritedSource.set(t, p.locationEntryId);
        }
      }
    }
  }

  // An own tick supersedes an inherited mark for display; exclusions win over
  // both (struck). Resolve overlaps so each type lands in exactly one bucket.
  for (const t of own) inherited.delete(t);
  for (const t of excluded) {
    own.delete(t);
    inherited.delete(t);
  }
  for (const t of inheritedSource.keys())
    if (!inherited.has(t)) inheritedSource.delete(t);

  return { own, inherited, excluded, inheritedSource };
}
